import React from 'react';
import { OrderDetailsDTO, getProgressStepIndex, PROGRESS_STEPS } from '../../types/order';
import StatusChip from '../shared/StatusChip';
import MoreActionsMenu from './MoreActionsMenu';

interface OrderHeaderProps {
  order: OrderDetailsDTO;
  onAction: (action: string) => void;
  onMoreActions: {
    edit: () => void;
    print: () => void;
    share: () => void;
    download: () => void;
    delete?: () => void;
  };
}

const OrderHeader: React.FC<OrderHeaderProps> = ({ order, onAction, onMoreActions }) => {
  const progressIndex = getProgressStepIndex(order.status);
  const progressPercentage = order.status === 'COMPLETED' ? 100 : (progressIndex + 1) * 20;
  
  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col gap-6">
          {/* Top Row: Order ID and Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Order ID and timestamp */}
            <div className="flex items-center gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" aria-label={`Order ${order.id}`}>
                #{order.id}
              </h1>
              <span className="text-sm text-gray-500 hidden sm:inline">
                Created {new Date(order.createdAt).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
            </div>
            
            {/* Right: Status and Actions */}
            <div className="flex items-center gap-3">
              <StatusChip status={order.status} />
              <button
                onClick={() => onAction('update-status')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm hover:shadow-md transition-all"
              >
                Update Status
              </button>
              <MoreActionsMenu
                onEdit={onMoreActions.edit}
                onPrint={onMoreActions.print}
                onShare={onMoreActions.share}
                onDownload={onMoreActions.download}
                onDelete={onMoreActions.delete}
              />
            </div>
          </div>
        
          {/* Progress Bar Row - Enhanced Design */}
          <div className="flex-1 pt-4">
            <div className="relative">
              {/* Progress Track Background */}
              <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>
            
            {/* Progress Steps */}
            <div className="relative flex items-start justify-between">
              {PROGRESS_STEPS.map((step, index) => {
                const isCompleted = index < progressIndex;
                const isCurrent = index === progressIndex;
                const isPending = index > progressIndex;
                
                return (
                  <div
                    key={step.id}
                    className="flex flex-col items-center flex-1 relative z-10"
                  >
                    {/* Step Circle */}
                    <div className="relative">
                      {/* Outer ring for current step */}
                      {isCurrent && (
                        <div className="absolute inset-0 rounded-full bg-blue-100 animate-pulse" style={{ width: '2.5rem', height: '2.5rem', marginTop: '-0.25rem', marginLeft: '-0.25rem' }}></div>
                      )}
                      
                      {/* Step indicator */}
                      <div
                        className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                          isCompleted
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                            : isCurrent
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-300 ring-4 ring-blue-100'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {isCompleted ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      
                      {/* Progress line to next step */}
                      {index < PROGRESS_STEPS.length - 1 && (
                        <div className="absolute top-5 left-full w-full h-1 -translate-x-1/2 z-0 rounded-full">
                          <div
                            className={`h-full transition-all duration-500 rounded-full ${
                              isCompleted ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                            style={{
                              width: isCompleted ? '100%' : isCurrent ? '50%' : '0%'
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                    
                    {/* Step Label */}
                    <div className="mt-3 text-center min-h-[2.5rem]">
                      <div
                        className={`text-xs font-medium transition-colors ${
                          isCurrent
                            ? 'text-blue-600 font-semibold'
                            : isCompleted
                            ? 'text-gray-700'
                            : 'text-gray-400'
                        }`}
                      >
                        <span className="hidden sm:inline">{step.label}</span>
                        <span className="sm:hidden">{step.label.split(' ')[0]}</span>
                      </div>
                      {isCurrent && (
                        <div className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                          {step.hint}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
            
            {/* Due Date */}
            {order.dueAt && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    Due: <span className="font-semibold text-gray-900">{new Date(order.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHeader;

